import React, {useState} from 'react'
import _ from 'lodash/fp'
import {Button, ContextMenu, Li, Loading, Icon} from '@startlibs/components'
import {Card, SignInLayout, CardHeader, PurviewFooter} from '../components/SigninLayout'
import {usePopupToggle, useToggle} from '@startlibs/core'
import styled, {css} from 'styled-components'
import {formatDate, getColor, media} from '@startlibs/utils'
import {authGetFetcher, getFetcherWithBearer, shareAuthGetFetcher} from '../utils/authFetch'
import {checkForDownload, downloadFile} from '../utils/downloadFile'
import {dicomDateToDate} from '../utils'
import {lighten} from 'polished'
import {DownloadErrorDialog} from './DownloadErrorDialog'
import {HelpButton} from "../components/HelpButton";
import {SPA_ORIGIN} from './ShareDialog'
import {DATE_FORMAT} from '../utils/DateFormat'

const fakeDownload = () => new Promise((res) => setTimeout(res, 2000))
const startedTimer = () => new Promise((res) => setTimeout(res, 4000))

const SharedRecordCard = styled(Card) `
  ${media.max(410)`
    padding: 2rem 1.5rem;
  `}
  ${Button} {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
`

const PatientContainer = styled.div`
  background-color: rgba(0,0,0,0.05);
  color: rgba(0,0,0,0.7);
  border-radius: 5px;
  margin-bottom: 1rem;
  padding: 1.25rem;
`
const PatientName = styled.div `
  font-weight: 600;
  font-size: 16px;
`
const PatientDob = styled.div`
  font-size: 13px;
  margin-top: 0.25rem;
`
const DownloadButton = styled(Button) `
  ${Loading} {
    display: inline-block;
    margin: -5px 6px -5px 0;
  }
  ${props => props.preparingDownload && css`
    background-color: ${props => lighten(0.525, props.theme.colors.main)};
    color: ${getColor('main')};
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    :after {
      content: none !important;
    }
  `}
  ${props => props.downloadStarted && css`
    background-color: ${props => lighten(0.46, props.theme.colors.success)};
    color: ${getColor('success')};
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    :after {
      content: none !important;
    }
  `}
  ${Icon} {
    margin-left: 4px;
    margin-right: -0.5rem;
    vertical-align: -1px;
    font-size: 11px;
  }
`
const CardActionsContainer = styled.div `
  margin: 1.5rem auto 0;
  display: flex;
  justify-content: center;
  ${Button} {
    flex-shrink: 1;
    flex-basis: 50%;
  }
  ${media.max(390)`
    display: block;
    ${Button} {
      width: 100%;
      & ~ ${Button} {
        margin-left: 0;
        margin-top: 0.75rem;
      }
    }
  `}
`

const tryOrFail = (test,fail) => {
  try {
    return test()
  } catch(e) {
    fail()
  }
}

const formatAvailableSignatures = _.update('success.availableSignatures',_.flow(_.map(v => [v.signatureId,v]),_.fromPairs))

export const SharedStudy = ({providerInfo,navigate,token,viewReport,openReport, downloadReport}) => {
  if (!token) {
    navigate('/')
    return null
  }

  const jwt = window.sessionStorage.getItem('sharejwt')
  const shareData = window.sessionStorage.getItem('sharedata')

  if (!jwt || !shareData) {
    navigate('/share/'+token)
    return null
  }

  const {patientName,patientDOB,reportJWT,allowImageDownload,shareStudyReport,...rest} = tryOrFail(() => JSON.parse(shareData),() => navigate('/'))

  const started = useToggle()

  const downloadLoading = useToggle()
  const viewLoading = useToggle()

  const reportLoading = useToggle()
  const reportViewer = useToggle()
  const [report,setReport] = useState()

  const downloadMenu = usePopupToggle()
  const downloadError = useToggle()

  const doOpenReport = () => {
    reportLoading.wait(
      openReport
        ? openReport({reportJWT,SPA_ORIGIN})
        : getFetcherWithBearer(reportJWT)(SPA_ORIGIN() + `/api/reports/view?nocache=${new Date().getTime()}`)
        .then(report => setReport(formatAvailableSignatures(report)))
        .then(reportViewer.open)
    )
  }

  const openViewer = () => {
    const wi = window.open('about:blank', '_blank')
    viewLoading.wait(
      shareAuthGetFetcher(SPA_ORIGIN() + `/api/share/viewerLink?nocache=${new Date().getTime()}`)
        .then(({viewerURL}) => { wi.location.href = viewerURL; })
        .catch(wi.close)
    )
  }

  const download = (downloadType) => () => {
    if (downloadType === 'report' && !downloadReport) { return }
    downloadLoading.wait(
      (
        downloadType === 'report'
        ? downloadReport({reportJWT,SPA_ORIGIN})
        : shareAuthGetFetcher(SPA_ORIGIN() + `/api/share/downloadLink/${downloadType}?nocache=${new Date().getTime()}`)
          .then(({url}) => downloadType === 'regular' ? url : checkForDownload(url))
      )
      .then(downloadFile)
    ).then(() => { started.open(); started.closeAfter(2000) })
      .catch(downloadError.open)
  }

  if (reportViewer.isOpen && report) {
    return viewReport({report:report.success,reportJWT,closeReport:reportViewer.close,openViewer})
  }

  return <SignInLayout providerInfo={providerInfo}>
    <SharedRecordCard>
      <CardHeader>
        <h1>Access shared records</h1>
      </CardHeader>
      <PatientContainer>
        <PatientName>Patient: {patientName}</PatientName>
        {!isNaN(dicomDateToDate(patientDOB).getTime()) && <PatientDob>Date of birth: {formatDate(dicomDateToDate(patientDOB), DATE_FORMAT(providerInfo.dateFormat))}</PatientDob>}
      </PatientContainer>
      <CardActionsContainer>
        {
          shareStudyReport && (viewReport||openReport) &&
          <Button.a highlight isLoading={reportLoading.isOpen} onClick={doOpenReport}>View report</Button.a>
        }
        <Button highlight onClick={openViewer}>View images</Button>
        {
          allowImageDownload &&
          <DownloadButton highlight preparingDownload={downloadLoading.isOpen} downloadStarted={started.isOpen}  onClick={downloadMenu.open} css="min-width: 120px;">
            {downloadLoading.isOpen ? <><Loading size={17} borderWidth={3}/>Preparing</> : (started.isOpen ? "Download started" : <>Download<Icon icon="arrow-down"/></>)}
            {
              downloadMenu.isOpen &&
              <ContextMenu>
                {shareStudyReport && <Li onClick={download('report')} href={downloadReport ? undefined : "/api/report/download?t="+reportJWT} label="Download report"/>}
                <Li onClick={download('regular')} label="Download images"/>
                <Li onClick={download('cd')} label={<>Download images for CD<br/>(with viewer)</>}/>
              </ContextMenu>
            }
          </DownloadButton>
        }
      </CardActionsContainer>
      {downloadError.isOpen && <DownloadErrorDialog closeDialog={downloadError.close}/>}
      <PurviewFooter/>
    </SharedRecordCard>
    <HelpButton />
  </SignInLayout>
}